/**
 * The shapes every inner page in the artboards repeats: the intro block, a section with a
 * heading, a card grid, and a card.
 *
 * These carry layout only. Every word on every page still comes from that page's artboard —
 * these just stop the same twelve Tailwind classes being retyped fifteen times, where they
 * would drift.
 */

import Link from 'next/link';
import type { ReactNode } from 'react';
import { PageEyebrow } from './PageEyebrow';

/**
 * The small mono line: the home page's "A disability-led studio · Victoria, Australia", every
 * inner page's eyebrow, and the breadcrumb trail. Annette asked for the three to be one style
 * (20 Oct 2026), so they read from here rather than each retyping the four classes.
 */
export const EYEBROW_CLASS = 'font-mono text-label tracking-[0.02em] text-muted';

/** The inner-page container: max 1200px, or 900px for a statement page. */
export function PageMain({
  children,
  narrow = false,
}: {
  children: ReactNode;
  narrow?: boolean;
}) {
  return (
    <div
      // One container for every page (§1). `narrow` no longer changes the CONTAINER - that
      // was a second left edge - it caps the reading measure of each section instead, which is
      // what the artboards draw: a 1200px main with 760px paragraphs inside it.
      className={`ax-container flex flex-col gap-2xl pb-[80px] pt-[56px] ${narrow ? '[&>*]:max-w-prose' : ''}`}
    >
      {children}
    </div>
  );
}

/**
 * Eyebrow, h1, lead, and whatever buttons the artboard puts under them.
 *
 * The eyebrow is optional. On the privacy policy and the terms it repeated the H1 word for word
 * — "Privacy policy" above "Privacy policy" — which a screen reader reads out twice before the
 * page starts (AXAI-SITE-008 Part 6). Omitting it renders nothing, not an empty paragraph.
 *
 * Since 21 Oct 2026 it is also dropped wherever a breadcrumb trail renders, because the trail
 * already says where the reader is — see PageEyebrow for what that looked like on /insights.
 */
export function PageIntro({
  eyebrow,
  title,
  lead,
  children,
}: {
  eyebrow?: string;
  title: string;
  lead?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section id="top" className="flex max-w-[820px] flex-col gap-5">
      <PageEyebrow>{eyebrow}</PageEyebrow>
      <h1 className="text-page-h1">{title}</h1>
      {lead ? <p className="m-0 text-lead-sm">{lead}</p> : null}
      {children ? <div className="flex flex-wrap gap-sm pt-xs">{children}</div> : null}
    </section>
  );
}

export function Section({
  id,
  heading,
  lead,
  children,
}: {
  id?: string;
  heading?: string;
  lead?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section id={id} className="flex flex-col gap-md">
      {heading ? <h2 className="text-h2-sm">{heading}</h2> : null}
      {lead ? <p className="m-0 max-w-[760px] text-intro text-muted">{lead}</p> : null}
      {children}
    </section>
  );
}

/**
 * `min` is the artboard's minmax() floor for that grid.
 *
 * Each is wrapped in min(..., 100%): a bare floor cannot shrink below itself, so at 360px any
 * floor above the ~296px of available width pushed the page sideways. This keeps the
 * artboard's column width on a wide screen and lets the track collapse on a phone
 * (AXAI-SITE-001 §3: nothing scrolls sideways).
 */
export function CardGrid({ min = 280, children }: { min?: number; children: ReactNode }) {
  const cls: Record<number, string> = {
    220: 'grid-cols-[repeat(auto-fit,minmax(min(220px,100%),1fr))]',
    240: 'grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))]',
    260: 'grid-cols-[repeat(auto-fit,minmax(min(260px,100%),1fr))]',
    280: 'grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))]',
    300: 'grid-cols-[repeat(auto-fit,minmax(min(300px,100%),1fr))]',
    320: 'grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))]',
  };
  return <div className={`grid gap-5 ${cls[min] ?? cls[280]}`}>{children}</div>;
}

/** A card. Give it `href` and the whole card becomes the link, as the artboards have it. */
export function Card({
  title,
  href,
  meta,
  children,
  accent = false,
}: {
  title?: string;
  href?: string;
  /** The small mono line at the foot of the card. */
  meta?: ReactNode;
  children?: ReactNode;
  /** The accent border the artboards use for the one step that needs a person. */
  accent?: boolean;
}) {
  // A card with an href IS a control, and brand v0.2 §6 is explicit: "Any edge a person needs to
  // find a control by ... uses border-strong". The decorative hairline is 1.3:1 and disappears for
  // anyone who needs contrast, which on a grid of tappable cards hides where each one begins.
  const cls = `flex min-w-0 flex-col gap-[10px] rounded-card bg-surface p-md text-ink no-underline ${
    accent
      ? 'border-2 border-accent'
      : href
        ? 'border border-borderStrong'
        : 'border border-border'
  }`;
  const inner = (
    <>
      {title ? <h3 className="text-h3-sm">{title}</h3> : null}
      {children}
      {meta ? <span className="font-mono text-chip font-normal text-muted">{meta}</span> : null}
    </>
  );
  if (!href) return <div className={cls}>{inner}</div>;
  const external = !href.startsWith('/') && !href.startsWith('#');
  return external ? (
    <a href={href} className={cls}>
      {inner}
    </a>
  ) : (
    <Link href={href} className={cls}>
      {inner}
    </Link>
  );
}

/** A card's body text. */
export function P({ children }: { children: ReactNode }) {
  return <p className="m-0 text-muted">{children}</p>;
}
